import {
  Attachment,
  Context,
  FinalCost,
  Intersection,
  Line,
  Material,
  MaterialType,
  Pannel,
  Province,
  Substraction,
} from "../types/canvasTypes";
import {
  DOOR_WIDTH,
  doorCost,
  glassList36H,
  glassList42H,
  glassList48H,
  glassList48R,
  glassListOtherR,
} from "../types/glassList";
import { MaterialMapBlack, MaterialMapSilver } from "../types/materialList";
import { redrawCanvas } from "./canvas";
import {
  checkLineIntersection,
  getAllIntersections,
  GlassAngle,
} from "./overlap";
import {
  calculateLineLength,
  calculateNumberOfGlass,
  calculateNumberOfGlassBasedOnBiggestGlass,
} from "./util";

function findSubstractionsBasedOnAngle(glass: GlassAngle) {
  if (glass.internal) {
    if (glass.angle < 30) {
      return 6;
    }
    if (glass.angle < 75) {
      return 5;
    }
    if (glass.angle < 100) {
      return 4;
    }
    if (glass.angle < 120) {
      return 3;
    }
    if (glass.angle < 140) {
      return 2;
    } else {
      return 1;
    }
  } else {
    if (glass.angle < 30) {
      return -4;
    }
    if (glass.angle < 75) {
      return -3;
    }
    if (glass.angle < 100) {
      return -2;
    }
    if (glass.angle < 120) {
      return -1;
    }
    if (glass.angle < 140) {
      return -0.5;
    } else {
      return 1;
    }
  }
}

// this function to find the substraction for all intersections.
function findSubstractions(intersections: Intersection[]) {
  const substractions: Substraction[] = [];
  for (let i = 0; i < intersections.length; i++) {
    const newSubstraction: Substraction = {
      line: intersections[i].line,
      substractions: 0,
    };

    // no intersection. Two walls
    if (intersections[i].intersections.length === 0) {
      newSubstraction.substractions = 2;
    }

    // one intersection. One wall
    else if (intersections[i].intersections.length === 1) {
      const glass = checkLineIntersection(
        intersections[i].line.parallelLine!,
        intersections[i].intersections[0].parallelLine!
      );
      newSubstraction.substractions = findSubstractionsBasedOnAngle(glass) + 1;
    }
    // two intersections. No wall
    else if (intersections[i].intersections.length === 2) {
      const glass1 = checkLineIntersection(
        intersections[i].line.parallelLine!,
        intersections[i].intersections[0].parallelLine!
      );
      const glass2 = checkLineIntersection(
        intersections[i].line.parallelLine!,
        intersections[i].intersections[1].parallelLine!
      );
      newSubstraction.substractions =
        findSubstractionsBasedOnAngle(glass1) +
        findSubstractionsBasedOnAngle(glass2);
    }
    substractions.push(newSubstraction);
  }

  return substractions;
}

// here we calulate the minimum number of glass needed to cover the line
// this function takes in the total number of glass needed
// the length of the closest glass and the length of the second closest
// creates all the combinations and take the combination with the smallest differenct with total length.
// then it returns the number of closest glass needed.
function calculateCombinationIdeal(
  totalNumberOfGlassNeeded: number,
  closest: number,
  secondClosest: number,
  totalLength: number
): number {
  const allCombinationsSum: number[] = [];
  const numberOfIdeaGlass: number[] = [];
  for (
    let numberOfIdealNeeded = 0;
    numberOfIdealNeeded <= totalNumberOfGlassNeeded;
    numberOfIdealNeeded += 1
  ) {
    const numberOfSecondGlass = totalNumberOfGlassNeeded - numberOfIdealNeeded;

    const sum =
      numberOfIdealNeeded * closest + numberOfSecondGlass * secondClosest;

    // if the difference between sum and total length is bigger than -0.1 add the abs to the list.
    if (totalLength - sum > -0.1 * totalNumberOfGlassNeeded) {
      allCombinationsSum.push(Math.abs(totalLength - sum));
      numberOfIdeaGlass.push(numberOfIdealNeeded);
    }
  }

  const minimumSum = Math.min(...allCombinationsSum);
  return numberOfIdeaGlass[allCombinationsSum.indexOf(minimumSum)];
}

const calculateBounds = (perfectglass: number, glassList: Pannel[]) => {
  let upperBound: Pannel | null = null;
  let lowerBound: Pannel | null = null;

  for (const value of glassList) {
    if (value.length >= perfectglass) {
      upperBound = value;
      break;
    }
    lowerBound = value;
  }

  return [lowerBound, upperBound];
};

function get48Pannels(
  line: Line, // total length of the line
  scale: number, // number of glass needed to cover the line
  glassDeduction: number, // deduction for the glass
  gapSize: number // gap size only works for chalk line
): Pannel[] {
  let length = calculateLineLength(line, scale);
  let numberOfGlass = calculateNumberOfGlass(line, scale);

  if (!length || length === undefined) throw Error("length is undefined");
  let tempLength = length - DOOR_WIDTH * line.numberOfDoors;

  let spaceNeeded =
    numberOfGlass * gapSize - 1 - (line.numberOfDoors > 0 ? 1 : 0);
  let deduction = spaceNeeded + glassDeduction;

  let totalLengthNeeded = tempLength - deduction;
  let idealGlass = totalLengthNeeded / numberOfGlass;

  const pannels: Pannel[] = [];
  let doorsNeeded = line.numberOfDoors;
  const [lowerBoundR, upperBoundR] = calculateBounds(idealGlass, glassList48R);
  const [lowerBound, upperBound] = calculateBounds(idealGlass, glassList48H);
  let numberOfIdealGlassNeeded = calculateCombinationIdeal(
    numberOfGlass,
    lowerBound ? lowerBound.length : 0,
    upperBound ? upperBound.length : 0,
    totalLengthNeeded
  );

  function addPanelsToCombination(
    bound: Pannel,
    boundR: Pannel | null,
    numberOfPanels: number
  ) {
    for (let i = 0; i < numberOfPanels; i += 1) {
      if (doorsNeeded > 0) {
        pannels.push(boundR ? boundR : bound);
        doorsNeeded--;
      } else {
        pannels.push(bound);
      }
    }
  }

  if (lowerBound) {
    addPanelsToCombination(lowerBound, lowerBoundR, numberOfIdealGlassNeeded);
  }

  // add second closest glass to the combination
  if (upperBound) {
    const remainingPanels = numberOfGlass - numberOfIdealGlassNeeded;
    addPanelsToCombination(upperBound, upperBoundR, remainingPanels);
  }

  return pannels;
}

function getPannels(
  line: Line, // total length of the line
  scale: number, // number of glass needed to cover the line
  glassDeduction: number, // deduction for the glass
  glassList: Pannel[], // list of glass
  gapSize: number
) {
  if (
    glassList[glassList.length - 1].price ===
    glassList48H[glassList48H.length - 1].price
  ) {
    return get48Pannels(line, scale, glassDeduction, gapSize);
  }
  let length = calculateLineLength(line, scale);

  if (!length || length === undefined) throw Error("length is undefined");
  let tempLength = length - DOOR_WIDTH * line.numberOfDoors;

  const pannels: Pannel[] = [];
  let doorsNeeded = line.numberOfDoors;

  while (doorsNeeded > 0) {
    pannels.push(glassListOtherR[0]);
    tempLength = tempLength - glassListOtherR[0].length;
    doorsNeeded--;
  }

  let numberOfGlass = calculateNumberOfGlassBasedOnBiggestGlass(tempLength, 73);
  let spaceNeeded = numberOfGlass - 1;

  let deduction = spaceNeeded * gapSize + glassDeduction;

  let totalLengthNeeded = tempLength - deduction;
  let idealGlass = totalLengthNeeded / numberOfGlass;

  const [lowerBound, upperBound] = calculateBounds(idealGlass, glassList);

  let numberOfIdealGlassNeeded = calculateCombinationIdeal(
    numberOfGlass,
    lowerBound ? lowerBound.length : 0,
    upperBound ? upperBound.length : 0,
    totalLengthNeeded
  );

  if (lowerBound) {
    for (let i = 0; i < numberOfIdealGlassNeeded; i += 1) {
      pannels.push(lowerBound);
    }
  }

  // add second closest glass to the combination
  if (upperBound) {
    for (let k = 0; k < numberOfGlass - numberOfIdealGlassNeeded; k += 1) {
      pannels.push(upperBound);
    }
  }

  return pannels;
}

export function getCost(lines: Line[], context: Context): Pannel[] {
  const pannels: Pannel[][] = [];
  if (!lines || !lines?.length || lines.length === 0) return [];

  let gap = context.gapSize || 1;

  if (!context.isChalkLine) {
    gap = 1;
  }

  const sharedLines = getAllIntersections(lines);
  let substractions = findSubstractions(sharedLines);
  for (let i = 0; i < substractions?.length; i++) {
    const pannel = getPannels(
      lines[i],
      context.scale,
      context.isChalkLine ? 2 * gap : substractions[i].substractions,
      context.glassList,
      gap
    );

    if (pannel) {
      lines[i].pannels = pannel;
      pannels.push(pannel);
    }
  }
  const flatten = pannels.flat();
  return flatten;
}

export function getAttachmentCost(lines: Line[]): Attachment[] {
  const intersections = getAllIntersections(lines);
  const attachments: Attachment[] = [];
  let pannel90Count = 0;
  let adjustableCount = 0;
  let WallAttachmentCount = 0;

  for (let i = 0; i < intersections.length; i++) {
    // No intersection -> 2 wall spigots
    if (intersections[i].intersections.length === 0) {
      WallAttachmentCount += 2;
    } // One intersection
    else if (intersections[i].intersections.length === 1) {
      const glassAngle = checkLineIntersection(
        intersections[i].line.parallelLine!,
        intersections[i].intersections[0].parallelLine!
      );
      WallAttachmentCount += 1;
      if (glassAngle.angle < 91 && glassAngle.angle > 89) {
        pannel90Count += 1;
      } else {
        adjustableCount += 1;
      }
    } // Two intersections
    else if (intersections[i].intersections.length === 2) {
      const glass1 = checkLineIntersection(
        intersections[i].line.parallelLine!,
        intersections[i].intersections[0].parallelLine!
      );

      const glass2 = checkLineIntersection(
        intersections[i].line.parallelLine!,
        intersections[i].intersections[1].parallelLine!
      );

      if (glass1.angle < 91 && glass1.angle > 89) {
        pannel90Count += 1;
      } else {
        adjustableCount += 1;
      }

      if (glass2.angle < 91 && glass2.angle > 89) {
        pannel90Count += 1;
      } else {
        adjustableCount += 1;
      }
    }
  }
  attachments.push({
    material: Material.AdjustableAttachement,
    count: adjustableCount / 2,
    costBlack: MaterialMapBlack.get(Material.AdjustableAttachement)!,
    costSilver: MaterialMapSilver.get(Material.AdjustableAttachement)!,
  });

  attachments.push({
    material: Material.Pannel90Attachemnt,
    count: pannel90Count / 2,
    costBlack: MaterialMapBlack.get(Material.Pannel90Attachemnt)!,
    costSilver: MaterialMapSilver.get(Material.Pannel90Attachemnt)!,
  });

  attachments.push({
    material: Material.WallAttachment,
    count: WallAttachmentCount,
    costBlack: MaterialMapBlack.get(Material.WallAttachment)!,
    costSilver: MaterialMapSilver.get(Material.WallAttachment)!,
  });

  return attachments;
}

function groupCosts(pannels: Pannel[]): FinalCost[] {
  const groupedPanels = new Map<string, Pannel[]>();
  // Iterate through the panels
  pannels.forEach((panel) => {
    // Generate a unique key based on length and isRPannel
    const key = `${panel.length}_${panel.isRPannel}`;

    // Check if the key already exists in the map
    if (groupedPanels.has(key)) {
      // If it exists, push the current panel to the existing group
      groupedPanels.get(key)!.push(panel);
    } else {
      // If it doesn't exist, create a new group with the current panel
      groupedPanels.set(key, [panel]);
    }
  });

  const finalCosts: FinalCost[] = [];

  groupedPanels.forEach((panels, _) => {
    finalCosts.push({
      length: panels[0].length,
      price: panels[0].price,
      total: panels.length,
      isRPannel: panels[0].isRPannel,
    });
  });

  return finalCosts;
}

function getSpigotsCost(pannels: Pannel[]): Attachment[] {
  let numberOfSpigotsNeeded = pannels.length * 2;
  const attachment: Attachment[] = [];

  attachment.push({
    material: Material.Spigots,
    count: numberOfSpigotsNeeded,
    costBlack: MaterialMapBlack.get(Material.Spigots)!,
    costSilver: MaterialMapSilver.get(Material.Spigots)!,
  });

  return attachment;
}

function getDoorCost(pannels: Pannel[]): {
  doorCost: FinalCost;
  attachement: Attachment[];
} {
  let count = 0;

  for (let i = 0; i < pannels.length; i++) {
    if (pannels[i].isRPannel) {
      count = count + 1;
    }
  }

  return {
    doorCost: {
      length: DOOR_WIDTH,
      price: doorCost,
      total: count,
      isDoor: true,
    },
    attachement: [
      {
        material: Material.DoorLatch,
        count: 1,
        costBlack: MaterialMapBlack.get(Material.DoorLatch)!,
        costSilver: MaterialMapSilver.get(Material.DoorLatch)!,
      },
      {
        material: Material.FloorLock,
        count: count,
        costBlack: MaterialMapBlack.get(Material.FloorLock)!,
        costSilver: MaterialMapSilver.get(Material.FloorLock)!,
      },
      {
        material: Material.HingeKit,
        count: count,
        costBlack: MaterialMapBlack.get(Material.HingeKit)!,
        costSilver: MaterialMapSilver.get(Material.HingeKit)!,
      },
    ],
  };
}

function getpannelsAttachements(
  attachment: Attachment[],
  numberOfPannels: number,
  doubleDoor: boolean
): Attachment {
  const attach90 =
    attachment.filter(
      (attachment) =>
        attachment.material === Material.Pannel90Attachemnt &&
        attachment.count > 0
    )?.[0]?.count || 0;

  const attachAdjust =
    attachment.filter(
      (attachment) =>
        attachment.material === Material.AdjustableAttachement &&
        attachment.count > 0
    )?.[0]?.count || 0;

  const wallAttachment =
    attachment.filter(
      (attachment) =>
        attachment.material === Material.WallAttachment && attachment.count > 0
    )?.[0]?.count || 0;

  const numberOfPannelAttatchments =
    numberOfPannels - wallAttachment / 2 - attach90 - attachAdjust;

  return {
    material: Material.Pannel180Attachemnt,
    count: numberOfPannelAttatchments - (doubleDoor === true ? 1 : 0),
    costBlack: MaterialMapBlack.get(Material.Pannel180Attachemnt)!,
    costSilver: MaterialMapSilver.get(Material.Pannel180Attachemnt)!,
  };
}

export function getFinalCost(
  pannels: Pannel[],
  material: MaterialType,
  attachment: Attachment[],
  includeAttachements: boolean = true,
  doubleDoor: boolean,
  province: Province
) {
  let totalCost = 0;
  const finalCost = groupCosts(pannels).sort((a, b) => a.length - b.length);
  const { doorCost, attachement: doorAttachement } = getDoorCost(pannels);

  let temp: Attachment[] = [];
  if (includeAttachements) {
    temp = [...attachment];
  }
  temp.push(...getSpigotsCost(pannels));

  if (doorCost.total > 0) {
    finalCost.push(doorCost);
    temp.push(...doorAttachement);
  }

  if (includeAttachements) {
    temp.push(getpannelsAttachements(attachment, pannels.length, doubleDoor));
  }

  for (let i = 0; i < finalCost.length; i++) {
    totalCost += finalCost[i].price.get(province)! * finalCost[i].total;
  }

  for (let i = 0; i < temp.length; i++) {
    if (material === MaterialType.Silver) {
      totalCost += temp[i].costSilver.get(province)! * temp[i].count;
    } else {
      totalCost += temp[i].costBlack.get(province)! * temp[i].count;
    }
  }

  return { finalCost, attachment: temp, totalCost };
}

export function getPannelList(localselectedOption: number): Pannel[] {
  if (localselectedOption === 42) {
    return glassList42H;
  } else if (localselectedOption === 36) {
    return glassList36H;
  } else {
    return glassList48H;
  }
}

export function renderChange(
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  lines: Line[],
  localselectedOption: number,
  scale: number,
  chalkLine: boolean,
  gap: number
): { pannels: Pannel[]; material: Attachment[] } {
  if (!canvas || !context || !lines || !lines?.length) {
    redrawCanvas(lines, context, canvas, scale);
    return { pannels: [], material: [] };
  }

  if (lines && lines.length === 0 && canvas && context) {
    redrawCanvas(lines, context, canvas, scale);
    return { pannels: [], material: [] };
  }

  if (!chalkLine) {
    gap = 1;
  }


  const pannels = getCost(lines, {
    glassList: getPannelList(localselectedOption),
    scale,
    isChalkLine: chalkLine,
    gapSize: gap,
  });
  const material = getAttachmentCost(lines);

  if (canvas && context) {
    redrawCanvas(lines, context, canvas, scale);
  }

  return { pannels, material };
}
